/**
 * Admin — Settings layout route (6 sub-tabs).
 *
 * Sub-tabs:
 *   /admin/settings              — General (default; also the two tenant settings)
 *   /admin/settings/compliance   — Compliance profile, residency, retention
 *   /admin/settings/api          — API keys, webhooks, delivery log
 *   /admin/settings/integrations — SIEM, SMTP, Chat Ops, SCIM, Storage
 *   /admin/settings/data         — Backup, export, deletion, legal hold
 */

import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  Building2, ShieldCheck, Key, Plug, Database,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/settings')({
  component: SettingsLayout,
});

const SUB_TABS = [
  { label: 'General',       path: '/admin/settings',              icon: Building2 },
  { label: 'Compliance',    path: '/admin/settings/compliance',   icon: ShieldCheck },
  { label: 'API & Webhooks', path: '/admin/settings/api',          icon: Key },
  { label: 'Integrations',  path: '/admin/settings/integrations', icon: Plug },
  { label: 'Data & Backup', path: '/admin/settings/data',         icon: Database },
];

function SettingsLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab bar */}
      <div className="border-b bg-background px-6 shrink-0">
        <nav className="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const isActive = location.pathname === t.path
              || (t.path !== '/admin/settings' && location.pathname.startsWith(t.path));
            const isExact = location.pathname === '/admin/settings' || location.pathname === '/admin/settings/';
            const active = t.path === '/admin/settings' ? isExact : isActive;
            return (
              <Link
                key={t.path}
                to={t.path as any}
                title={t.label}
                aria-label={t.label}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
